import { z } from 'zod';
import { CaptureRefSchema } from './capture';

// =====================
// Note Enums
// =====================

export enum NoteVisibilityEnum {
  PRIVATE = 'PRIVATE',
  SHARED = 'SHARED',
  TEAM = 'TEAM',
  RECORD = 'RECORD',
}

export enum NoteMemberRoleEnum {
  VIEWER = 'VIEWER',
  EDITOR = 'EDITOR',
  ADMIN = 'ADMIN',
}

export enum NotePageSourceEnum {
  MANUAL = 'MANUAL',
  TRANSCRIPTION = 'TRANSCRIPTION',
  AI = 'AI',
}

// =====================
// Zod Schemas
// =====================

export const NoteVisibilitySchema = z.nativeEnum(NoteVisibilityEnum);
export const NoteMemberRoleSchema = z.nativeEnum(NoteMemberRoleEnum);
export const NotePageSourceSchema = z.nativeEnum(NotePageSourceEnum);

export const NoteSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  creatorId: z.string().uuid(),
  categoryId: z.string().uuid().optional().nullable(),
  title: z.string().default('Untitled'),
  // Content is stored as binary (Yjs state) but transferred as base64 string or Uint8Array
  // Using z.any() since actual type varies between server (Buffer) and client (Uint8Array/null)
  content: z.any().optional().nullable(),
  contentHtml: z.string().optional().nullable(),
  visibility: NoteVisibilitySchema.default(NoteVisibilityEnum.PRIVATE),
  isPinned: z.boolean().default(false),
  color: z.string().max(7).optional().nullable(),
  // Collection support - when true, content lives in NotePage children
  isCollection: z.boolean().default(false),
  pageCount: z.number().default(0), // Denormalized count for display
  moduleType: z.string().optional().nullable(),
  moduleId: z.string().uuid().optional().nullable(),
  totalMembers: z.number().optional(), // Populated when loading notes with member counts
  // T1 — capture provenance (U7 badge). Server-managed, write-once at
  // ingestion; null for records created before T1 or outside a wired producer.
  captureId: z.string().uuid().nullable().optional(),
  capture: CaptureRefSchema.nullable().optional(),
  // Archive support - archived notes hide from default lists, restorable
  archivedAt: z.date().optional().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().optional().nullable(),
});

export const NoteMemberSchema = z.object({
  id: z.string().uuid(),
  noteId: z.string().uuid(),
  userId: z.string().uuid(),
  role: NoteMemberRoleSchema.default(NoteMemberRoleEnum.VIEWER),
  isSynced: z.boolean().default(false), // True if auto-managed by RECORD visibility
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const NotePageSchema = z.object({
  id: z.string().uuid(),
  noteId: z.string().uuid(),
  title: z.string().max(500).default('Untitled'),
  // Content is stored as binary (Yjs state) but transferred as base64 string or Uint8Array
  content: z.any().optional().nullable(),
  contentHtml: z.string().optional().nullable(),
  contentPreview: z.string().max(500).optional().nullable(), // First ~200 chars for list view
  order: z.number().default(0),
  // Source tracking (for AI-generated pages)
  source: NotePageSourceSchema.default(NotePageSourceEnum.MANUAL),
  sourceMetadata: z.record(z.any()).optional().nullable(), // { meetingId, recordingUrl, duration, etc }
  createdAt: z.date(),
  updatedAt: z.date(),
  createdBy: z.string().uuid().optional().nullable(),
});

// Summary version for list view (without content)
export const NotePageSummarySchema = z.object({
  id: z.string().uuid(),
  noteId: z.string().uuid(),
  title: z.string(),
  contentPreview: z.string().optional().nullable(),
  order: z.number(),
  source: NotePageSourceSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
  createdBy: z.string().uuid().optional().nullable(),
});

// =====================
// Create/Update DTOs
// =====================

export const CreateNoteSchema = z.object({
  title: z.string().min(1).max(255).optional().default('Untitled'),
  contentHtml: z.string().optional(),
  visibility: NoteVisibilitySchema.optional().default(NoteVisibilityEnum.PRIVATE),
  isPinned: z.boolean().optional().default(false),
  color: z.string().max(7).optional(),
  moduleType: z.string().optional(),
  moduleId: z.string().uuid().optional(),
  categoryId: z.string().uuid().optional(),
  isCollection: z.boolean().optional().default(false),
});

export const UpdateNoteSchema = z.object({
  title: z.string().min(1).max(255).optional(),
  contentHtml: z.string().optional(),
  visibility: NoteVisibilitySchema.optional(),
  isPinned: z.boolean().optional(),
  // true archives the note (sets archivedAt), false restores it
  isArchived: z.boolean().optional(),
  color: z.string().max(7).optional().nullable(),
  moduleType: z.string().optional().nullable(),
  moduleId: z.string().uuid().optional().nullable(),
  categoryId: z.string().uuid().optional().nullable(),
});

// Update Note input schema/type for API + UI payloads
export const UpdateNoteInputSchema = UpdateNoteSchema;

export const AddNoteMemberSchema = z.object({
  userId: z.string().uuid(),
  role: NoteMemberRoleSchema.optional().default(NoteMemberRoleEnum.VIEWER),
});

export const UpdateNoteMemberSchema = z.object({
  role: NoteMemberRoleSchema,
});

// Note Page DTOs
export const CreateNotePageSchema = z.object({
  title: z.string().max(500).optional().default('Untitled'),
  source: NotePageSourceSchema.optional().default(NotePageSourceEnum.MANUAL),
  sourceMetadata: z.record(z.any()).optional(),
});

export const UpdateNotePageSchema = z.object({
  title: z.string().max(500).optional(),
  contentHtml: z.string().optional(),
});

export const ReorderNotePagesSchema = z.object({
  pageIds: z.array(z.string().uuid()),
});

// =====================
// TypeScript Types
// =====================

export type NoteVisibility = z.infer<typeof NoteVisibilitySchema>;
export type NoteMemberRole = z.infer<typeof NoteMemberRoleSchema>;
export type NotePageSource = z.infer<typeof NotePageSourceSchema>;
export type Note = z.infer<typeof NoteSchema>;
export type NoteMember = z.infer<typeof NoteMemberSchema>;
export type NotePage = z.infer<typeof NotePageSchema>;
export type NotePageSummary = z.infer<typeof NotePageSummarySchema>;
export type CreateNote = z.infer<typeof CreateNoteSchema>;
export type UpdateNote = z.infer<typeof UpdateNoteSchema>;
export type UpdateNoteInput = z.infer<typeof UpdateNoteInputSchema>;
export type AddNoteMember = z.infer<typeof AddNoteMemberSchema>;
export type UpdateNoteMember = z.infer<typeof UpdateNoteMemberSchema>;
export type CreateNotePage = z.infer<typeof CreateNotePageSchema>;
export type UpdateNotePage = z.infer<typeof UpdateNotePageSchema>;
export type ReorderNotePages = z.infer<typeof ReorderNotePagesSchema>;

// =====================
// Extended Types with Relations
// =====================

export interface NotePageWithCreator extends NotePage {
  creator?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    displayName: string | null;
    email: string;
    image: string | null;
  } | null;
}

export interface NotePageSummaryWithCreator extends NotePageSummary {
  creator?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    displayName: string | null;
    image: string | null;
  } | null;
}

export interface NoteWithRelations extends Note {
  creator?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    displayName: string | null;
    email: string;
    image: string | null;
  };
  members?: (NoteMember & {
    user: {
      id: string;
      firstName: string | null;
      lastName: string | null;
      displayName: string | null;
      email: string;
      image: string | null;
    };
  })[];
  category?: {
    id: string;
    name: string;
    color: string | null;
    userId: string | null; // null = org category, set = private
  } | null;
  totalMembers?: number;
  currentUserRole?: NoteMemberRole | null;
  moduleInfo?: {
    id: string;
    name: string;
    type: string;
  } | null;
  tags?: {
    id: string;
    name: string;
    color: string | null;
  }[];
  // For collection notes - page summaries (without full content)
  pages?: NotePageSummaryWithCreator[];
}

// =====================
// Yjs Sync Types
// =====================

export interface YjsUpdate {
  noteId: string;
  pageId?: string; // Optional - for page-specific documents
  update: Uint8Array; // Yjs binary update
  origin?: string; // Client ID or user ID
}

export interface YjsAwarenessUpdate {
  noteId: string;
  pageId?: string; // Optional - for page-specific documents
  added: number[];
  updated: number[];
  removed: number[];
  states: Map<number, any>;
}

// Page-specific Yjs update
export interface YjsPageUpdate {
  noteId: string;
  pageId: string;
  update: Uint8Array;
  origin?: string;
}

// =====================
// Note Query Filters
// =====================

export interface NoteFilters {
  search?: string;
  visibility?: NoteVisibility;
  creatorId?: string;
  isPinned?: boolean;
  moduleType?: string;
  moduleId?: string;
  categoryId?: string;
  hasMembers?: boolean;
  createdAfter?: Date;
  createdBefore?: Date;
}

// =====================
// Note Category Schemas & Types
// =====================

export const NoteCategorySchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  moduleType: z.string().optional().nullable(),
  moduleId: z.string().uuid().optional().nullable(),
  userId: z.string().uuid().optional().nullable(), // null = org category, set = private user category
  name: z.string().max(255),
  description: z.string().optional().nullable(),
  icon: z.string().max(50).optional().nullable(),
  color: z.string().max(7).optional().nullable(),
  order: z.number().default(0),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().optional().nullable(),
});

export const CreateNoteCategorySchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().optional(),
  color: z.string().max(7).optional(),
  icon: z.string().max(50).optional(),
  moduleType: z.string().optional(),
  moduleId: z.string().uuid().optional(),
  isPrivate: z.boolean().optional().default(false), // If true, creates with current userId
});

export const UpdateNoteCategorySchema = z.object({
  name: z.string().min(1).max(255).optional(),
  description: z.string().optional().nullable(),
  color: z.string().max(7).optional().nullable(),
  icon: z.string().max(50).optional().nullable(),
  order: z.number().optional(),
});

export type NoteCategory = z.infer<typeof NoteCategorySchema>;
export type CreateNoteCategory = z.infer<typeof CreateNoteCategorySchema>;
export type UpdateNoteCategory = z.infer<typeof UpdateNoteCategorySchema>;

export interface NoteCategoryWithCount extends NoteCategory {
  notesCount: number;
  isPrivate: boolean; // Computed: userId !== null
}

// =====================
// Record Access Sync Types
// =====================

/**
 * Source of access for a user to a record
 */
export type RecordAccessSource = 'module_permission' | 'project_member' | 'record_permission' | 'org_admin';

/**
 * User with access to a record (used for RECORD visibility sync)
 */
export interface RecordAccessUser {
  userId: string;
  displayName?: string;
  email?: string;
  image?: string | null;
  role: NoteMemberRoleEnum;
  source: RecordAccessSource;
}

/**
 * Result of syncing note access for RECORD visibility
 */
export interface RecordAccessSyncResult {
  noteId: string;
  added: string[];    // user IDs added
  removed: string[];  // user IDs removed
  updated: string[];  // user IDs with role changes
}

// =====================
// Note Snapshot Types
// =====================

export const NoteSnapshotSchema = z.object({
  id: z.string().uuid(),
  noteId: z.string().uuid(),
  pageId: z.string().uuid().optional().nullable(),
  content: z.any(), // Binary Yjs state
  contentHash: z.string(),
  title: z.string(),
  createdBy: z.string().uuid(),
  createdAt: z.date(),
});

export type NoteSnapshot = z.infer<typeof NoteSnapshotSchema>;

export interface NoteSnapshotWithCreator extends NoteSnapshot {
  creator?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    displayName: string | null;
    email: string;
    image: string | null;
  };
}
